import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemsIntegrated = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const systems = [
    {
      id: 1,
      name: 'SAP ERP',
      type: 'Enterprise Resource Planning',
      status: 'active',
      lastSync: '2026-02-10T02:30:00',
      version: '7.5.2',
      endpoint: 'https://sap.businessanalytics.com/api',
      description: 'Core financial and operational data integration for revenue, cost, and resource management',
    },
    {
      id: 2,
      name: 'Salesforce CRM',
      type: 'Customer Relationship Management',
      status: 'active',
      lastSync: '2026-02-10T02:28:00',
      version: '2024.1',
      endpoint: 'https://salesforce.businessanalytics.com/api',
      description: 'Customer data, sales pipeline, and revenue forecasting integration',
    },
    {
      id: 3,
      name: 'Workday HCM',
      type: 'Human Capital Management',
      status: 'active',
      lastSync: '2026-02-10T02:25:00',
      version: '2024.R2',
      endpoint: 'https://workday.businessanalytics.com/api',
      description: 'Employee data, performance metrics, and workforce analytics integration',
    },
    {
      id: 4,
      name: 'Jira Project Management',
      type: 'Project & Task Management',
      status: 'warning',
      lastSync: '2026-02-10T01:45:00',
      version: '9.12.0',
      endpoint: 'https://jira.businessanalytics.com/api',
      description: 'Project execution, delivery metrics, and throughput data integration',
    },
    {
      id: 5,
      name: 'Power BI Analytics',
      type: 'Business Intelligence',
      status: 'active',
      lastSync: '2026-02-10T02:32:00',
      version: '2.125.0',
      endpoint: 'https://powerbi.businessanalytics.com/api',
      description: 'Advanced analytics and visualization data pipeline integration',
    },
    {
      id: 6,
      name: 'Azure Data Lake',
      type: 'Data Storage',
      status: 'inactive',
      lastSync: '2026-02-09T18:20:00',
      version: 'Gen2',
      endpoint: 'https://azure.businessanalytics.com/api',
      description: 'Historical data storage and archival system integration',
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      case 'inactive':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'CheckCircle2';
      case 'warning':
        return 'AlertTriangle';
      case 'inactive':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredSystems = systems?.filter((system) => {
    const matchesSearch =
      system?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      system?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' || system?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <div className="relative flex-1">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search systems..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="warning">Warning</option>
            <option value="inactive">Inactive</option>
          </select>
          <Button variant="default" iconName="Plus" iconPosition="left">
            Add System
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {filteredSystems?.map((system) => (
          <div
            key={system?.id}
            className="bg-card border border-border rounded-md p-4 md:p-6 hover:shadow-md transition-smooth"
          >
            <div className="flex items-start justify-between mb-3 md:mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-1">
                  {system?.name}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground font-caption">
                  {system?.type}
                </p>
              </div>
              <div
                className={`flex items-center gap-1.5 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium font-caption ${getStatusColor(
                  system?.status
                )}`}
              >
                <Icon name={getStatusIcon(system?.status)} size={14} />
                <span className="capitalize">{system?.status}</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
              {system?.description}
            </p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-muted-foreground font-caption">
                  Version:
                </span>
                <span className="text-foreground font-medium font-data">
                  {system?.version}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-muted-foreground font-caption">
                  Last Sync:
                </span>
                <span className="text-foreground font-medium">
                  {formatTimestamp(system?.lastSync)}
                </span>
              </div>
              <div className="flex items-start justify-between text-xs md:text-sm">
                <span className="text-muted-foreground font-caption">
                  Endpoint:
                </span>
                <span className="text-foreground font-medium font-data text-right break-all ml-2">
                  {system?.endpoint}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" iconName="Settings">
                Configure
              </Button>
              <Button variant="ghost" size="sm" iconName="RefreshCw">
                Sync Now
              </Button>
              <Button variant="ghost" size="sm" iconName="Activity">
                Logs
              </Button>
            </div>
          </div>
        ))}
      </div>
      {filteredSystems?.length === 0 && (
        <div className="text-center py-12 md:py-16">
          <Icon
            name="Database"
            size={48}
            className="mx-auto mb-4 text-muted-foreground"
          />
          <p className="text-muted-foreground">No systems found</p>
        </div>
      )}
    </div>
  );
};

export default SystemsIntegrated;